/**
 * @file Maintenance linked-Equipment lookup and display UI.
 */

import { appData } from "../../app/state/state.js";
import {
  escapeHTML,
  sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
  FIELD_WRAPPER_CLASSES,
  NATIVE_CHECKBOX_CLASSES,
  NATIVE_CHECKBOX_ICON_CLASSES,
  NATIVE_CHECKBOX_LABEL_CLASSES,
  NATIVE_CHECKBOX_MARKER_CLASSES,
  NATIVE_INPUT_CLASSES,
  bindNativeFieldBehavior,
  getFieldLabelHtml,
  getReadOnlyValueHtml,
} from "../../shared/forms/nativeFieldStyles.js";
import {
  attachLinkedParentFieldListeners,
  LINKED_PARENT_ACTIONS,
} from "../../shared/details/linkedParentSection.js";
import {
  lookupMaintenanceAssetCandidates,
} from "./maintenanceApi.js";

const MODAL_ID = "maintenance-asset-lookup-modal";
const RESULT_LIMIT = 20;

function getAssetTypeConfig() {
  return appData?.config?.assetTypes || [];
}

function getAssetTypeName(typeId) {
  return getAssetTypeConfig().find((entry) => entry.id === typeId)?.name ||
    typeId;
}

function attrValue(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function getKnownAsset(assetId) {
  if (!assetId) {return null;}
  return (appData?.assets || []).find((asset) => asset.id === assetId) || null;
}

/**
 * Resolves the safest available display data for linked Equipment.
 * @param {object} source Maintenance item/form state.
 * @returns {object} Display data.
 */
export function getMaintenanceAssetDisplay(source = {}) {
  const assetId = source?.linked_asset_id || "";
  const asset = getKnownAsset(assetId);
  return {
    id: assetId,
    name: asset?.name || source?.linked_asset_name || source?.item || "",
    serialNumber: asset?.serial_number || source?.linked_asset_serial_number || "",
    types: Array.isArray(asset?.types) ?
      asset.types :
      (Array.isArray(source?.linked_asset_types) ? source.linked_asset_types : []),
  };
}

/**
 * Renders the linked Equipment field used by Maintenance create/detail surfaces.
 * @param {object} params Field params.
 * @param {object} params.source Maintenance item/form state.
 * @param {boolean} [params.canOpen=false] Whether Equipment detail open is enabled.
 * @param {boolean} [params.canLink=true] Whether link/change is enabled.
 * @param {boolean} [params.canUnlink=true] Whether unlink is enabled.
 * @returns {string} Field HTML.
 */
export function renderMaintenanceAssetLinkField({
  source = {},
  canOpen = false,
  canLink = true,
  canUnlink = true,
} = {}) {
  const display = getMaintenanceAssetDisplay(source);
  const hasLink = Boolean(display.id);
  const lowChromeActionClasses = [
    "inline-flex",
    "h-6",
    "w-6",
    "items-center",
    "justify-center",
    "rounded-full",
    "text-gray-400",
    "transition-colors",
    "hover:text-[var(--dfc-brand-primary)]",
    "focus:outline-none",
    "focus:text-[var(--dfc-brand-primary)]",
  ].join(" ");
  const unlinkActionClasses = [
    "inline-flex",
    "h-6",
    "w-6",
    "items-center",
    "justify-center",
    "rounded-full",
    "text-gray-400",
    "transition-colors",
    "hover:text-red-600",
    "focus:outline-none",
    "focus:text-red-600",
  ].join(" ");

  if (!hasLink) {
    return `
      <div class="md:col-span-2 grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4" data-linked-parent-field="maintenance-linked-asset">
        <input type="hidden" name="linked_asset_id" value="">
        <input type="hidden" name="linked_asset_name" value="">
        <div class="${FIELD_WRAPPER_CLASSES}">
          ${getFieldLabelHtml("Link")}
          <button
            type="button"
            class="flex min-h-10 w-full items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-left text-base font-medium text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)] sm:min-h-[30px] sm:text-sm"
            data-linked-parent-action="${attrValue(LINKED_PARENT_ACTIONS.LINK)}"
            data-linked-parent-field="maintenance-linked-asset"
            data-linked-parent-id=""
            data-linked-parent-type="asset"
            aria-label="Link to an equipment item"
            ${canLink ? "" : "disabled"}
          >
            Link to an equipment item
          </button>
        </div>
        <div class="flex min-w-0 items-end md:self-stretch">
          <p class="text-xs leading-5 text-gray-500">
            If this maintenance ticket relates to clinical equipment, it should be linked to it
          </p>
        </div>
      </div>
    `;
  }

  return `
    <div class="md:col-span-2 grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4" data-linked-parent-field="maintenance-linked-asset">
      <input type="hidden" name="linked_asset_id" value="${attrValue(display.id)}">
      <input type="hidden" name="linked_asset_name" value="${attrValue(display.name)}">
      <div class="${FIELD_WRAPPER_CLASSES}">
        ${getFieldLabelHtml("Equipment")}
        ${getReadOnlyValueHtml(sanitizeHTML(display.name || "Linked equipment"))}
      </div>
      <div class="flex min-w-0 items-start justify-start gap-0.5 md:pt-6">
        <button
          type="button"
          class="${lowChromeActionClasses}"
          data-linked-parent-action="${attrValue(LINKED_PARENT_ACTIONS.LINK)}"
          data-linked-parent-field="maintenance-linked-asset"
          data-linked-parent-id="${attrValue(display.id)}"
          data-linked-parent-type="asset"
          title="Change linked equipment"
          aria-label="Change linked equipment"
          ${canLink ? "" : "disabled"}
        >
          <span class="material-symbols-outlined pointer-events-none text-base leading-none">sync_alt</span>
        </button>
        ${canOpen ? `
          <button
            type="button"
            class="${lowChromeActionClasses}"
            data-linked-parent-action="${attrValue(LINKED_PARENT_ACTIONS.OPEN)}"
            data-linked-parent-field="maintenance-linked-asset"
            data-linked-parent-id="${attrValue(display.id)}"
            data-linked-parent-type="asset"
            title="Open linked equipment"
            aria-label="Open linked equipment"
          >
            <span class="material-symbols-outlined pointer-events-none text-base leading-none">open_in_new</span>
          </button>
        ` : ""}
        <button
          type="button"
          class="${unlinkActionClasses}"
          data-linked-parent-action="${attrValue(LINKED_PARENT_ACTIONS.UNLINK)}"
          data-linked-parent-field="maintenance-linked-asset"
          data-linked-parent-id="${attrValue(display.id)}"
          data-linked-parent-type="asset"
          title="Unlink equipment"
          aria-label="Unlink equipment"
          ${canUnlink ? "" : "disabled"}
        >
          <span class="material-symbols-outlined pointer-events-none text-base leading-none">link_off</span>
        </button>
      </div>
    </div>
  `;
}

function ensureModal() {
  let modal = document.getElementById(MODAL_ID);
  if (modal) {return modal;}

  modal = document.createElement("div");
  modal.id = MODAL_ID;
  modal.className = "fixed inset-0 z-50 hidden items-center justify-center bg-black/40 p-4";
  modal.innerHTML = `
    <div class="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
      <div class="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
        <h2 class="text-lg font-semibold text-gray-900" data-maintenance-asset-title>Link Equipment</h2>
        <button type="button" class="maintenance-asset-lookup-close standard-action-icon-btn standard-action-icon-btn--danger shrink-0" aria-label="Close equipment lookup">
          <span class="material-symbols-outlined text-[20px] leading-none">close</span>
        </button>
      </div>
      <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
        <form data-maintenance-asset-search-form>
          <label class="min-w-0">
            <span class="mb-1 block text-sm font-medium text-gray-700">Search Equipment</span>
            <input
              type="search"
              name="assetSearch"
              class="${NATIVE_INPUT_CLASSES}"
              placeholder="Name or serial number"
              autocomplete="off"
            >
          </label>
        </form>
        <div data-maintenance-asset-type-filters></div>
        <div data-maintenance-asset-message class="hidden rounded-md border px-3 py-2 text-sm"></div>
        <div data-maintenance-asset-results class="space-y-2"></div>
      </div>
      <div class="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
        <button type="button" class="maintenance-asset-lookup-close inline-flex h-9 items-center justify-center rounded-md bg-gray-200 px-4 text-sm font-semibold text-gray-800 hover:bg-gray-300">
          Cancel
        </button>
      </div>
    </div>
  `;
  document.body.append(modal);
  return modal;
}

function setModalMessage(modal, message, tone = "neutral") {
  const element = modal.querySelector("[data-maintenance-asset-message]");
  if (!element) {return;}

  if (!message) {
    element.className = "hidden rounded-md border px-3 py-2 text-sm";
    element.textContent = "";
    return;
  }

  const toneClasses = tone === "error" ?
    "border-red-200 bg-red-50 text-red-700" :
    tone === "success" ?
      "border-green-200 bg-green-50 text-green-700" :
      "border-gray-200 bg-gray-50 text-gray-600";
  element.className = `rounded-md border px-3 py-2 text-sm ${toneClasses}`;
  element.textContent = message;
}

function renderTypeFilters(modal, selectedTypeIds = []) {
  const root = modal.querySelector("[data-maintenance-asset-type-filters]");
  if (!root) {return;}
  const typeOptions = getAssetTypeConfig()
    .filter((entry) => entry?.id && entry?.isActive !== false)
    .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999) ||
      String(a.name || a.id).localeCompare(String(b.name || b.id)));

  if (typeOptions.length === 0) {
    root.innerHTML = "";
    return;
  }

  const selected = new Set(selectedTypeIds.map(String));
  root.innerHTML = `
    <div class="text-sm">
      <div class="mb-2 font-medium text-gray-700">Equipment Type</div>
      <div class="flex flex-wrap gap-2">
        ${typeOptions.map((type) => `
          <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
            <input type="checkbox" value="${attrValue(type.id)}" class="${NATIVE_CHECKBOX_CLASSES} maintenance-asset-type-filter" ${selected.has(String(type.id)) ? "checked" : ""}>
            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
              <span class="${NATIVE_CHECKBOX_ICON_CLASSES}" data-native-checkbox-icon>check</span>
            </span>
            <span>${sanitizeHTML(type.name || type.id)}</span>
          </label>
        `).join("")}
      </div>
    </div>
  `;
}

function getSelectedTypeIds(modal) {
  return Array.from(modal.querySelectorAll(".maintenance-asset-type-filter:checked"))
    .map((input) => input.value)
    .filter(Boolean);
}

function renderTypeBadges(typeIds = []) {
  const ids = Array.isArray(typeIds) ? typeIds : [];
  if (ids.length === 0) {return "";}
  return `
    <div class="mt-2 flex flex-wrap gap-1">
      ${ids.map((typeId) => `
        <span class="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
          ${sanitizeHTML(getAssetTypeName(typeId))}
        </span>
      `).join("")}
    </div>
  `;
}

function renderResults(modal, results = [], selectedAssetId = "") {
  const root = modal.querySelector("[data-maintenance-asset-results]");
  if (!root) {return;}

  if (results.length === 0) {
    root.innerHTML = `
      <div class="rounded-md border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
        No active matching equipment found.
      </div>
    `;
    return;
  }

  root.innerHTML = results.map((asset) => {
    const isSelected = selectedAssetId && asset.id === selectedAssetId;
    const serial = !isSelected && asset.serial_number ?
      `Serial ${asset.serial_number}` :
      "";
    return `
      <button
        type="button"
        class="maintenance-asset-result flex w-full items-start justify-between gap-3 rounded-md border border-gray-200 bg-white p-3 text-left transition-colors hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[var(--dfc-brand-primary-ring)]"
        data-asset-id="${escapeHTML(asset.id)}"
      >
        <span class="min-w-0">
          <span class="block truncate text-sm font-semibold text-gray-900">${sanitizeHTML(asset.name || "Unnamed Equipment")}</span>
          ${serial ? `<span class="mt-1 block text-xs text-gray-500">${sanitizeHTML(serial)}</span>` : ""}
          ${renderTypeBadges(asset.types)}
        </span>
        ${isSelected ? `
          <span class="inline-flex shrink-0 items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">
            <span class="material-symbols-outlined text-[14px] leading-none">check</span>
            Linked
          </span>
        ` : ""}
      </button>
    `;
  }).join("");
}

function getSelectedAssetResult(selectedAssetId = "", selectedAsset = null) {
  if (!selectedAssetId) {return null;}

  const asset = getKnownAsset(selectedAssetId);
  if (asset) {return asset;}

  const fallback = selectedAsset || {};
  const fallbackName = String(fallback.name || fallback.linked_asset_name || "").trim();
  if (!fallbackName) {return null;}

  return {
    id: selectedAssetId,
    name: fallbackName,
    serial_number: fallback.serialNumber || fallback.serial_number || "",
    types: Array.isArray(fallback.types) ? fallback.types : [],
  };
}

function getLookupErrorMessage(error) {
  if (error?.code === "permission-denied") {
    return "You do not have permission to look up equipment for Maintenance.";
  }
  if (error?.code === "invalid-argument") {
    return error.message || "The lookup request was not valid.";
  }
  if (error?.code === "unauthenticated") {
    return "Sign in again before looking up equipment.";
  }
  return error?.message || "Equipment lookup failed. Please try again.";
}

/**
 * Opens the Maintenance Equipment lookup modal.
 * @param {object} params Modal params.
 * @param {string} [params.selectedAssetId] Currently linked Equipment id.
 * @param {object|null} [params.selectedAsset] Current linked Equipment display data.
 * @param {Function} params.onSelect Callback when a result is selected.
 * @returns {void}
 */
export function openMaintenanceAssetLookupModal({
  selectedAssetId = "",
  selectedAsset = null,
  onSelect,
} = {}) {
  const modal = ensureModal();
  const form = modal.querySelector("[data-maintenance-asset-search-form]");
  const searchInput = modal.querySelector('input[name="assetSearch"]');
  let latestResults = [];
  let searchTimer = null;

  const close = () => {
    modal.classList.add("hidden");
    modal.classList.remove("flex");
  };

  const runSearch = async () => {
    const search = searchInput?.value || "";
    const typeIds = getSelectedTypeIds(modal);
    const resultsRoot = modal.querySelector("[data-maintenance-asset-results]");
    if (resultsRoot) {
      resultsRoot.innerHTML = `
        <div class="rounded-md border border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
          Searching...
        </div>
      `;
    }
    setModalMessage(modal, "");

    try {
      const response = await lookupMaintenanceAssetCandidates({
        search,
        typeIds,
        limit: RESULT_LIMIT,
      });
      latestResults = Array.isArray(response?.results) ? response.results : [];
      renderResults(modal, latestResults, selectedAssetId);
      if (latestResults.length === 0) {
        setModalMessage(modal, "No active matching equipment found.", "neutral");
      }
    } catch (error) {
      latestResults = [];
      renderResults(modal, [], selectedAssetId);
      setModalMessage(modal, getLookupErrorMessage(error), "error");
    }
  };

  modal.querySelectorAll(".maintenance-asset-lookup-close").forEach((button) => {
    button.onclick = close;
  });
  form.onsubmit = (event) => {
    event.preventDefault();
    void runSearch();
  };
  if (searchInput) {
    searchInput.oninput = () => {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => {
        void runSearch();
      }, 250);
    };
  }

  renderTypeFilters(modal, []);
  bindNativeFieldBehavior(modal);
  modal.querySelectorAll(".maintenance-asset-type-filter").forEach((input) => {
    input.onchange = () => void runSearch();
  });
  const currentAsset = getSelectedAssetResult(selectedAssetId, selectedAsset);
  if (currentAsset) {
    latestResults = [currentAsset];
    renderResults(modal, latestResults, selectedAssetId);
  } else {
    modal.querySelector("[data-maintenance-asset-results]").innerHTML = `
      <div class="rounded-md border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
        Search by equipment name, serial number, or type.
      </div>
    `;
  }
  setModalMessage(modal, "");
  if (searchInput) {searchInput.value = "";}
  modal.classList.remove("hidden");
  modal.classList.add("flex");
  requestAnimationFrame(() => searchInput?.focus());

  modal.querySelector("[data-maintenance-asset-results]").onclick = async (event) => {
    const button = event.target.closest(".maintenance-asset-result");
    if (!button) {return;}
    const asset = latestResults.find((entry) => entry.id === button.dataset.assetId);
    if (!asset || typeof onSelect !== "function") {return;}

    button.disabled = true;
    setModalMessage(modal, "");
    try {
      const result = await onSelect(asset);
      if (result === false) {
        button.disabled = false;
        return;
      }
      close();
    } catch (error) {
      button.disabled = false;
      setModalMessage(modal, error?.message || "Could not link this equipment.", "error");
    }
  };
}

/**
 * Attaches shared linked-Equipment field action listeners.
 * @param {HTMLElement} container Root container.
 * @param {object} handlers Handler callbacks.
 * @param {Function} [handlers.onLink] Link/change action.
 * @param {Function} [handlers.onOpen] Open linked Equipment action.
 * @param {Function} [handlers.onUnlink] Clear linked Equipment action.
 * @returns {void}
 */
export function attachMaintenanceAssetLinkFieldListeners(container, handlers = {}) {
  attachLinkedParentFieldListeners(container, {
    [LINKED_PARENT_ACTIONS.LINK]: handlers.onLink,
    [LINKED_PARENT_ACTIONS.OPEN]: handlers.onOpen,
    [LINKED_PARENT_ACTIONS.UNLINK]: handlers.onUnlink,
  });
}
